import React from 'react';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import { SelectInputRecord } from 'types/dynamic-forms/records/SelectInputRecord';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import useProperty from '../hooks/useProperty';
import usePropertyPath from '../hooks/usePropertyPath';
import useDataSource from '../hooks/useDataSource';
import { useDocumentStore } from '../hooks/useDocumentStore';

const SelectInputRenderer: React.FC<SelectInputRecord> = ({
  propertyId,
  label,
  tooltip,
  helpText,
  setValuesOnSelect,
}) => {
  const store = useDocumentStore();
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, isVisible, errorMessage, isDisabled } = useProperty<string | null>(propertyPath);

  const { options } = useDataSource(propertyPath);
  if (!isVisible) {
    return null;
  }

  const handleChange = (newValue: string | null) => {
    const previousValue = value;
    setValue(newValue ?? null);

    if (!setValuesOnSelect) {
      return;
    }
    // Set derived properties when the selection enters `onValue`, clear them when it leaves.
    for (const rule of setValuesOnSelect) {
      if (newValue === rule.onValue && previousValue !== rule.onValue) {
        const derived = rule.valueFromPropertyId ? store.getValue([rule.valueFromPropertyId]) : rule.value;
        store.setValue([rule.propertyId], derived ?? null);
      } else if ((rule.clearOnLeave ?? true) && previousValue === rule.onValue && newValue !== rule.onValue) {
        store.setValue([rule.propertyId], null);
      }
    }
  };

  return (
    <WizardSelectControlledComponent
      name={propertyId}
      options={options}
      value={options.find((o) => o.value === value) ?? null}
      label={label}
      tooltip={tooltip}
      helpText={helpText}
      onChanged={(_: string, value: any) => handleChange(value ?? null)}
      error={errorMessage}
      disabled={isDisabled}
      {...reactSelectPropsForHtmlLabel}
    />
  );
};

export default SelectInputRenderer;
